// Operations (spec §13, §33, §34; ADR 0013): facilities and spaces, the
// durable asset register, work orders and inspections. The vocabularies,
// the shapes core returns and the bodies it accepts live here, so core,
// the web Worker and the staff UI agree on them.
//
// Three boundaries hold throughout:
//   - Booking stays authoritative for room reservations and availability.
//     Hub stores no reservation and never infers availability.
//   - An asset is durable equipment. POS keeps consumable stock and
//     Booking keeps rental inventory; neither is mirrored here.
//   - A work order's cost is a note for the people doing the work. It is
//     not accounting and never posts to QuickBooks.

import { z } from 'zod'
import type { SpaceBookingImport, SpaceOrigin, SpaceRoomFlag } from './space-import'

const DAY = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD')
const ID = z.string().min(1).max(100)
const text = (max: number) => z.string().trim().max(max)
const optionalText = (max: number) => text(max).nullable().optional()
const CENTS = z.number().int().min(0).max(100_000_000_00)

// --------------------------------------------------------------- facilities

export const FACILITY_KINDS = ['venue', 'outdoor', 'office', 'storage', 'other'] as const
export type FacilityKind = (typeof FACILITY_KINDS)[number]
export const FACILITY_KIND_LABELS: Record<FacilityKind, string> = { venue: 'Venue', outdoor: 'Outdoor site', office: 'Office', storage: 'Storage', other: 'Other' }

export const SPACE_KINDS = ['hall', 'room', 'stage', 'lobby', 'backstage', 'concessions', 'kitchen', 'office', 'storage', 'outdoor', 'other'] as const
export type SpaceKind = (typeof SPACE_KINDS)[number]
export const SPACE_KIND_LABELS: Record<SpaceKind, string> = {
  hall: 'Hall',
  room: 'Room',
  stage: 'Stage',
  lobby: 'Lobby',
  backstage: 'Backstage',
  concessions: 'Concessions',
  kitchen: 'Kitchen',
  office: 'Office',
  storage: 'Storage',
  outdoor: 'Outdoor area',
  other: 'Other',
}

export type PlaceStatus = 'active' | 'inactive'

// fromBooking: kept equal to the room's capacity in Booking by the import
// (ADR 0025); changed only in Booking.
export type SpaceConfiguration = { id: string; name: string; capacity: number; notes: string | null; fromBooking: boolean }

// A pointer at a record in another app (a Booking room), picked from
// Booking's room list or typed (ADR 0013, 0023). Hub reads the room's
// reservations live from Booking through it and stores none of them; it
// never answers whether the room is free.
export type SpaceSourceLink = {
  id: string
  sourceService: 'booking'
  entityType: 'room'
  sourceId: string
  label: string
  linkedAt: string
  linkedByName: string | null
}

export type FacilitySummary = {
  id: string
  name: string
  kind: FacilityKind
  status: PlaceStatus
  address: string | null
  spaceCount: number
}

export type SpaceSummary = {
  id: string
  facilityId: string
  facilityName: string
  parentSpaceId: string | null
  parentSpaceName: string | null
  name: string
  kind: SpaceKind
  status: PlaceStatus
  // The largest capacity among its layouts; null when it has none.
  maxCapacity: number | null
  configurations: SpaceConfiguration[]
  // Made by the import from a Booking room, made in Hub and linked to a
  // room by hand, or Hub-only (ADR 0025).
  origin: SpaceOrigin
}

// A Booking venue mapped to a facility (ADR 0025).
export type FacilityVenueLink = { linkId: string; venue: string; method: 'picked' | 'suggestion' | 'created'; linkedAt: string; linkedByName: string | null }

export type Facility = FacilitySummary & { notes: string | null; createdAt: string; updatedAt: string; spaces: SpaceSummary[]; bookingVenues: FacilityVenueLink[] }
export type Space = SpaceSummary & { notes: string | null; createdAt: string; updatedAt: string; children: { id: string; name: string; kind: SpaceKind; status: PlaceStatus }[]
  bookingRoom: SpaceSourceLink | null
  // Set when the import made this space from a Booking room: Booking then
  // keeps its name, status and capacities (ADR 0025).
  bookingImport: SpaceBookingImport | null
  // Set when the linked room went inactive or away in Booking.
  roomFlag: SpaceRoomFlag | null
}

// A Hub event at a facility, optionally in one of its spaces.
export type EventPlace = { id: string; facilityId: string; facilityName: string; spaceId: string | null; spaceName: string | null }
// One row per event, however many of the place's spaces it is linked to:
// `spaces` lists them (empty when it is linked to the whole facility only),
// and `wholeFacility` says it is also linked to the facility itself.
export type PlaceEvent = { linkId: string; eventId: string; name: string; startsOn: string; endsOn: string; status: string; wholeFacility: boolean; spaces: { spaceId: string; spaceName: string }[] }

export const FacilityBody = z.object({
  name: text(200).min(1),
  kind: z.enum(FACILITY_KINDS),
  status: z.enum(['active', 'inactive']).optional(),
  address: optionalText(500),
  notes: optionalText(4000),
})
export const SpaceBody = z.object({
  name: text(200).min(1),
  kind: z.enum(SPACE_KINDS),
  status: z.enum(['active', 'inactive']).optional(),
  parentSpaceId: ID.nullable().optional(),
  notes: optionalText(4000),
})
export const SpaceConfigurationBody = z.object({ name: text(120).min(1), capacity: z.number().int().min(0).max(1_000_000), notes: optionalText(1000) })
export const SpaceSourceLinkBody = z.object({
  sourceService: z.literal('booking'),
  entityType: z.literal('room'),
  sourceId: text(200).min(1),
  label: text(300).min(1),
})
export const EventPlaceBody = z.object({ facilityId: ID, spaceId: ID.nullable().optional() })

// ------------------------------------------------------------------- assets

export const ASSET_STATUSES = ['in_service', 'out_for_repair', 'checked_out', 'retired', 'lost'] as const
export type AssetStatus = (typeof ASSET_STATUSES)[number]
export const ASSET_STATUS_LABELS: Record<AssetStatus, string> = { in_service: 'In service', out_for_repair: 'Out for repair', checked_out: 'Checked out', retired: 'Retired', lost: 'Lost' }

export const ASSET_CONDITIONS = ['new', 'good', 'fair', 'poor', 'unusable'] as const
export type AssetCondition = (typeof ASSET_CONDITIONS)[number]
export const ASSET_CONDITION_LABELS: Record<AssetCondition, string> = { new: 'New', good: 'Good', fair: 'Fair', poor: 'Poor', unusable: 'Unusable' }

// Human-friendly and safe in a URL: BRE-00042, KEY-12, MA-LIFT-1.
export const ASSET_TAG_PATTERN = /^[A-Z0-9][A-Z0-9-]{1,23}$/
export const ASSET_TAG_PREFIX = 'BRE-'
export const normalizeAssetTag = (value: string) => value.trim().toUpperCase().replace(/\s+/g, '-')

export type AssetCategory = { id: string; name: string; retiredAt: string | null; assetCount: number }

export type AssetSummary = {
  id: string
  name: string
  tag: string
  categoryId: string
  categoryName: string
  status: AssetStatus
  condition: AssetCondition | null
  facilityId: string | null
  facilityName: string | null
  spaceId: string | null
  spaceName: string | null
  serialNumber: string | null
  // Who or what has it now, when it is checked out.
  holder: { personId: string | null; personName: string | null; eventId: string | null; eventName: string | null; dueBackOn: string | null } | null
}

export type Asset = AssetSummary & {
  make: string | null
  model: string | null
  purchaseDate: string | null
  purchaseCostCents: number | null
  currency: 'USD'
  vendorOrganizationId: string | null
  vendorName: string | null
  warrantyExpiresOn: string | null
  replacementYear: number | null
  replacementCostCents: number | null
  notes: string | null
  createdAt: string
  updatedAt: string
}

export type AssetCheckout = {
  id: string
  assetId: string
  assetName: string
  assetTag: string
  personId: string | null
  personName: string | null
  eventId: string | null
  eventName: string | null
  checkedOutAt: string
  checkedOutByName: string | null
  dueBackOn: string | null
  note: string | null
  returnedAt: string | null
  returnedByName: string | null
  returnCondition: AssetCondition | null
  returnNote: string | null
}

const TAG = z
  .string()
  .max(40)
  .transform(normalizeAssetTag)
  .pipe(z.string().regex(ASSET_TAG_PATTERN, 'Use 2 to 24 letters, digits and dashes, such as BRE-00042'))

export const AssetBody = z.object({
  name: text(200).min(1),
  tag: TAG,
  categoryId: ID,
  // Checked out is set only by a check-out, never by an edit.
  status: z.enum(['in_service', 'out_for_repair', 'retired', 'lost']).optional(),
  condition: z.enum(ASSET_CONDITIONS).nullable().optional(),
  facilityId: ID.nullable().optional(),
  spaceId: ID.nullable().optional(),
  serialNumber: optionalText(120),
  make: optionalText(120),
  model: optionalText(120),
  purchaseDate: DAY.nullable().optional(),
  purchaseCostCents: CENTS.nullable().optional(),
  vendorOrganizationId: ID.nullable().optional(),
  warrantyExpiresOn: DAY.nullable().optional(),
  replacementYear: z.number().int().min(2000).max(2200).nullable().optional(),
  replacementCostCents: CENTS.nullable().optional(),
  notes: optionalText(4000),
})

export const CheckoutBody = z
  .object({
    personId: ID.nullable().optional(),
    eventId: ID.nullable().optional(),
    dueBackOn: DAY.nullable().optional(),
    note: optionalText(1000),
  })
  .refine((b) => !!b.personId || !!b.eventId, { message: 'Check it out to a person, an event, or both' })

export const ReturnBody = z.object({
  condition: z.enum(ASSET_CONDITIONS),
  note: optionalText(1000),
  // What the asset is after the return. It comes back in service unless
  // it needs repair or never came back.
  status: z.enum(['in_service', 'out_for_repair', 'lost']).default('in_service'),
})

// Preventive maintenance. A schedule belongs to one asset, or to a
// category as the default for every asset in it that has none of its own.
// It repeats on an interval from its start date, or falls on fixed dates.
export const INTERVAL_UNITS = ['days', 'months'] as const
export type IntervalUnit = (typeof INTERVAL_UNITS)[number]

export type MaintenanceSchedule = {
  id: string
  title: string
  instructions: string | null
  assetId: string | null
  assetName: string | null
  assetTag: string | null
  categoryId: string | null
  categoryName: string | null
  intervalValue: number | null
  intervalUnit: IntervalUnit | null
  startsOn: string | null
  fixedDates: string[]
  // A work order opens this many days before each due date.
  leadDays: number
  assigneePersonId: string | null
  assigneeName: string | null
  retiredAt: string | null
  // The next date no work order has been opened for yet; null when a
  // fixed-date schedule has run out of dates.
  nextDueOn: string | null
}

export const MaintenanceScheduleBody = z
  .object({
    title: text(300).min(1),
    instructions: optionalText(4000),
    assetId: ID.nullable().optional(),
    categoryId: ID.nullable().optional(),
    intervalValue: z.number().int().min(1).max(3650).nullable().optional(),
    intervalUnit: z.enum(INTERVAL_UNITS).nullable().optional(),
    startsOn: DAY.nullable().optional(),
    fixedDates: z.array(DAY).max(36).optional(),
    leadDays: z.number().int().min(0).max(60).default(7),
    assigneePersonId: ID.nullable().optional(),
  })
  .refine((b) => !!b.assetId !== !!b.categoryId, { message: 'A schedule is for one asset or one category' })
  .refine((b) => (b.intervalValue != null && b.intervalUnit != null && !!b.startsOn) !== ((b.fixedDates?.length ?? 0) > 0), {
    message: 'Give an interval with a start date, or fixed dates, not both',
  })

// The dates a schedule falls due after `after` (exclusive), soonest first.
// An interval schedule is a fixed cadence from its start date, so a late
// work order never moves the next one.
export function nextOccurrence(
  schedule: { intervalValue: number | null; intervalUnit: IntervalUnit | null; startsOn: string | null; fixedDates: string[] },
  after: string | null,
): string | null {
  if (schedule.intervalValue && schedule.intervalUnit && schedule.startsOn) {
    if (after === null || after < schedule.startsOn) return schedule.startsOn
    for (let n = 1; n < 100_000; n += 1) {
      const candidate = stepDate(schedule.startsOn, schedule.intervalValue * n, schedule.intervalUnit)
      if (candidate > after) return candidate
    }
    return null
  }
  return [...schedule.fixedDates].sort().find((d) => after === null || d > after) ?? null
}

// The occurrence to act on today: the latest one that has come due
// (allowing `leadDays` of notice) after `after`. Occurrences missed in
// between are skipped, so a schedule that starts in the past, or a cron
// that was off for a month, opens one work order, not a backlog.
export function dueOccurrence(
  schedule: { intervalValue: number | null; intervalUnit: IntervalUnit | null; startsOn: string | null; fixedDates: string[] },
  after: string | null,
  today: string,
  leadDays: number,
): string | null {
  const horizon = stepDate(today, leadDays, 'days')
  let due: string | null = null
  let cursor = after
  for (let guard = 0; guard < 100_000; guard += 1) {
    const next = nextOccurrence(schedule, cursor)
    if (next === null || next > horizon) break
    due = next
    cursor = next
  }
  return due
}

// A calendar date moved by days or whole months. A month step from the
// 31st lands on the last day of a shorter month.
export function stepDate(day: string, amount: number, unit: IntervalUnit): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number]
  if (unit === 'days') return new Date(Date.UTC(y, m - 1, d + amount)).toISOString().slice(0, 10)
  const first = new Date(Date.UTC(y, m - 1 + amount, 1))
  const last = new Date(Date.UTC(first.getUTCFullYear(), first.getUTCMonth() + 1, 0)).getUTCDate()
  return `${first.getUTCFullYear()}-${String(first.getUTCMonth() + 1).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`
}

// Capital planning: what is due for replacement, by year. An asset with no
// estimate is counted apart: missing is not zero.
export type ReplacementYear = { year: number; assets: number; estimatedCents: number; withoutEstimate: number }

// -------------------------------------------------------------- work orders

export const WORK_ORDER_TYPES = ['repair', 'preventive_maintenance', 'setup', 'reset', 'damage', 'other'] as const
export type WorkOrderType = (typeof WORK_ORDER_TYPES)[number]
export const WORK_ORDER_TYPE_LABELS: Record<WorkOrderType, string> = {
  repair: 'Repair',
  preventive_maintenance: 'Preventive maintenance',
  setup: 'Setup',
  reset: 'Reset',
  damage: 'Damage follow-up',
  other: 'Other',
}

export const WORK_ORDER_STATUSES = ['open', 'assigned', 'in_progress', 'waiting', 'done', 'canceled'] as const
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number]
export const WORK_ORDER_STATUS_LABELS: Record<WorkOrderStatus, string> = { open: 'Open', assigned: 'Assigned', in_progress: 'In progress', waiting: 'Waiting', done: 'Done', canceled: 'Canceled' }
export const WORK_ORDER_OPEN_STATUSES: WorkOrderStatus[] = ['open', 'assigned', 'in_progress', 'waiting']

export const WORK_ORDER_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const
export type WorkOrderPriority = (typeof WORK_ORDER_PRIORITIES)[number]

export type WorkOrderSummary = {
  id: string
  number: number
  title: string
  type: WorkOrderType
  priority: WorkOrderPriority
  status: WorkOrderStatus
  dueOn: string | null
  facilityId: string | null
  facilityName: string | null
  spaceId: string | null
  spaceName: string | null
  assetId: string | null
  assetName: string | null
  assetTag: string | null
  eventId: string | null
  eventName: string | null
  assigneePersonId: string | null
  assigneeName: string | null
  requesterPersonId: string | null
  requesterName: string | null
  createdAt: string
  completedAt: string | null
}

export type WorkOrder = WorkOrderSummary & {
  description: string | null
  completionNotes: string | null
  laborMinutes: number | null
  // A note for the people doing the work. Not accounting: it never posts
  // to QuickBooks.
  partsNote: string | null
  partsCostCents: number | null
  // For damage follow-up: where the damage was recorded, as typed (a
  // Booking rental id, say). Hub never checks or writes to that app.
  sourceReference: string | null
  maintenanceScheduleId: string | null
  maintenanceDueOn: string | null
  inspectionRunId: string | null
  inspectionItemLabel: string | null
  setupTemplateId: string | null
  // A follow-up to an incident (ADR 0015): its id only.
  incidentId: string | null
  // The vendor who did the work (ADR 0016).
  vendorOrganizationId: string | null
  vendorName: string | null
  createdByName: string | null
  updatedAt: string
  version: number
  // What the viewer may do with this one.
  can: { manage: boolean; work: boolean }
}

const WorkOrderFields = {
  title: text(300).min(1),
  description: optionalText(4000),
  type: z.enum(WORK_ORDER_TYPES),
  priority: z.enum(WORK_ORDER_PRIORITIES).optional(),
  facilityId: ID.nullable().optional(),
  spaceId: ID.nullable().optional(),
  assetId: ID.nullable().optional(),
  eventId: ID.nullable().optional(),
  requesterPersonId: ID.nullable().optional(),
  assigneePersonId: ID.nullable().optional(),
  dueOn: DAY.nullable().optional(),
  sourceReference: optionalText(300),
  // The vendor who did the work (ADR 0016), an organization.
  vendorOrganizationId: ID.nullable().optional(),
}
export const WorkOrderBody = z.object({
  ...WorkOrderFields,
  // Set when the work order comes from a failed inspection item.
  inspectionRunId: ID.optional(),
  inspectionItemKey: text(60).min(1).optional(),
})

// An edit by someone who manages work orders: any field.
export const WorkOrderPatch = z.object({
  ...WorkOrderFields,
  title: WorkOrderFields.title.optional(),
  type: WorkOrderFields.type.optional(),
  status: z.enum(WORK_ORDER_STATUSES).optional(),
  completionNotes: optionalText(4000),
  laborMinutes: z.number().int().min(0).max(100_000).nullable().optional(),
  partsNote: optionalText(2000),
  partsCostCents: CENTS.nullable().optional(),
  expectedVersion: z.number().int().min(1),
})

// What the person a work order is assigned to may change with
// workorders.work alone. Anything else in the body is refused.
export const WORK_ORDER_WORK_FIELDS = ['status', 'completionNotes', 'laborMinutes', 'partsNote', 'partsCostCents'] as const

export const WorkOrderNoteBody = z.object({ body: text(2000).min(1) })

// A setup or reset task list kept per facility, turned into work orders
// for an event from the event's page.
export const SETUP_TEMPLATE_KINDS = ['setup', 'reset'] as const
export type SetupTemplateKind = (typeof SETUP_TEMPLATE_KINDS)[number]

export type SetupTemplate = {
  id: string
  facilityId: string
  facilityName: string
  spaceId: string | null
  spaceName: string | null
  kind: SetupTemplateKind
  title: string
  description: string | null
  // Setup: days from the event's first day. Reset: days from its last.
  offsetDays: number
  priority: WorkOrderPriority
  retiredAt: string | null
}

export const SetupTemplateBody = z.object({
  kind: z.enum(SETUP_TEMPLATE_KINDS),
  title: text(300).min(1),
  description: optionalText(4000),
  spaceId: ID.nullable().optional(),
  offsetDays: z.number().int().min(-60).max(60).default(0),
  priority: z.enum(WORK_ORDER_PRIORITIES).default('normal'),
})

export const EventSetupBody = z.object({ templateIds: z.array(ID).min(1).max(50), assigneePersonId: ID.nullable().optional() })

// -------------------------------------------------------------- inspections

export const INSPECTION_SUBJECT_TYPES = ['facility', 'space', 'asset_category'] as const
export type InspectionTemplateSubject = (typeof INSPECTION_SUBJECT_TYPES)[number]
export const INSPECTION_OUTCOMES = ['pass', 'fail', 'na'] as const
export type InspectionOutcome = (typeof INSPECTION_OUTCOMES)[number]
export const INSPECTION_OUTCOME_LABELS: Record<InspectionOutcome, string> = { pass: 'Pass', fail: 'Fail', na: 'N/A' }
export const INSPECTION_RUN_STATUSES = ['scheduled', 'in_progress', 'completed', 'canceled'] as const
export type InspectionRunStatus = (typeof INSPECTION_RUN_STATUSES)[number]
export const INSPECTION_RUN_STATUS_LABELS: Record<InspectionRunStatus, string> = { scheduled: 'Scheduled', in_progress: 'In progress', completed: 'Completed', canceled: 'Canceled' }

export type InspectionItem = { key: string; label: string; help: string | null }

export type InspectionTemplate = {
  id: string
  name: string
  subjectType: InspectionTemplateSubject
  subjectId: string
  subjectLabel: string | null
  items: InspectionItem[]
  // Every this many days a run is scheduled; null means by hand only.
  recurrenceDays: number | null
  startsOn: string | null
  assigneePersonId: string | null
  assigneeName: string | null
  version: number
  retiredAt: string | null
  nextDueOn: string | null
}

export type InspectionResult = {
  itemKey: string
  outcome: InspectionOutcome
  note: string | null
  photoDocumentId: string | null
  recordedAt: string
  recordedByName: string | null
}

export type InspectionRunSummary = {
  id: string
  templateId: string
  templateName: string
  status: InspectionRunStatus
  origin: 'scheduled' | 'manual' | 'correction'
  // What is being inspected. A run of a category template started by hand
  // can name one asset of that category.
  subjectType: InspectionTemplateSubject | 'asset'
  subjectId: string
  subjectLabel: string | null
  dueOn: string | null
  assigneePersonId: string | null
  assigneeName: string | null
  startedAt: string | null
  completedAt: string | null
  completedByName: string | null
  itemCount: number
  answered: number
  failed: number
  correctsRunId: string | null
  correctedByRunId: string | null
  createdAt: string
}

export type InspectionRun = InspectionRunSummary & {
  // The checklist as it was when the run began. Editing the template
  // later never changes a run.
  items: InspectionItem[]
  results: InspectionResult[]
  notes: string | null
  facilityId: string | null
  spaceId: string | null
  assetId: string | null
  workOrders: { id: string; number: number; title: string; status: WorkOrderStatus; itemKey: string | null }[]
}

const InspectionItemBody = z.object({ key: z.string().regex(/^[a-z0-9_]{1,40}$/).optional(), label: text(300).min(1), help: optionalText(500) })

export const InspectionTemplateBody = z
  .object({
    name: text(200).min(1),
    subjectType: z.enum(INSPECTION_SUBJECT_TYPES),
    subjectId: ID,
    items: z.array(InspectionItemBody).min(1).max(100),
    recurrenceDays: z.number().int().min(1).max(3650).nullable().optional(),
    startsOn: DAY.nullable().optional(),
    assigneePersonId: ID.nullable().optional(),
  })
  .refine((b) => b.recurrenceDays == null || !!b.startsOn, { message: 'A repeating inspection needs a first date' })

export const StartInspectionBody = z.object({ templateId: ID, assetId: ID.nullable().optional(), dueOn: DAY.nullable().optional(), assigneePersonId: ID.nullable().optional() })

export const InspectionResultBody = z.object({
  outcome: z.enum(INSPECTION_OUTCOMES),
  note: optionalText(1000),
  photoDocumentId: ID.nullable().optional(),
})

export const CompleteInspectionBody = z.object({ notes: optionalText(2000) })

// The scheduled job that opens preventive-maintenance work orders and
// schedules repeating inspections signs as this subject, with no person.
// Core lets it do nothing else.
export const OPERATIONS_SYSTEM_SUB = 'system:operations'

export type OperationsRunResult = { today: string; workOrdersOpened: number; inspectionsScheduled: number; errors: string[] }
